<template>
    <div class="page-dunning-settings">
        <DRow>
            <DCol>
                <DTabs class="mb-3">
                    <DTabItem
                        id="dunning-general"
                        :route-url="$route('settings.dunning-settings.create')"
                        :active="['settings.dunning-settings.create'].includes($route().current())"
                        :label="__('settings.dunning_settings.tabs.general')"
                    />
                    <DTabItem
                        id="dunning-fees-and-limits"
                        :route-url="$route('settings.dunning-settings.fees-and-limits.index')"
                        :active="['settings.dunning-settings.fees-and-limits.index', 'settings.dunning-settings.fees-and-limits.edit'].includes($route().current())"
                        :label="__('settings.dunning_settings.tabs.fees_and_limits')"
                    />
                </DTabs>
            </DCol>
        </DRow>
        <DCard
            :title="__('settings.dunning_settings.form.general')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.attach_documents')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.attach_documents_hint')"
            >
                <DMultiSelect
                    id="attach_documents"
                    :options="getDunningAttachDocumentsOptions"
                    :multiple="true"
                    :selection="true"
                    selection-type="text"
                    :is-valid="(value) => validField('attach_documents', value)"
                    @update="(value) => updateDunningAttachDocumentsValue(value)"
                />
            </DFormRow>
        </DCard>
        <DCard
            :title="__('settings.dunning_settings.form.first_reminder')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.first_grace_period')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.first_grace_period_hint')"
            >
                <DInput
                    id="first_grace_period"
                    v-model="form.first_grace_period"
                    add-label-classes="required"
                    :is-valid="(value) => validField('first_grace_period', value)"
                    type="number"
                    min="0"
                    max="255"
                />
                <DAlert
                    v-if="errors && errors.first_grace_period"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.first_grace_period }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.first_due_days')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.first_due_days_hint')"
            >
                <DInput
                    id="first_due_days"
                    v-model="form.first_due_days"
                    add-label-classes="required"
                    :is-valid="(value) => validField('first_due_days', value)"
                    type="number"
                    min="0"
                    max="255"
                />
                <DAlert
                    v-if="errors && errors.first_due_days"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.first_due_days }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.enable_first_reminder_pdf')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.enable_reminder_pdf_helptext')"
            >
                <DSwitch
                    v-model="form.enable_first_reminder_pdf"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DCard
                :title="__('settings.dunning_settings.form.delivery_type')"
                padding
            >
                <DRow class="mx-auto mb-3">
                    {{ __('settings.dunning_settings.form.delivery_type_hint') }}
                </DRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_email')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_email_hint_first')"
                >
                    <DSwitch
                        v-model="form.delivery_type_email"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_postal')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_postal_hint_first')"
                >
                    <DSwitch
                        v-model="form.delivery_type_postal"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_electronic_invoice')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_electronic_invoice_hint')"
                >
                    <DSwitch
                        v-model="form.delivery_type_electronic_invoice"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_sms')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_sms_hint_first')"
                >
                    <DSwitch
                        v-model="form.delivery_type_sms"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_use_customer_settings')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_use_customer_settings_hint')"
                >
                    <DSwitch
                        v-model="form.delivery_type_use_customer_settings"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
        </DCard>
        <DCard
            :title="__('settings.dunning_settings.form.second_reminder')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.second_grace_period')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.second_grace_period_hint')"
            >
                <DInput
                    id="second_grace_period"
                    v-model="form.second_grace_period"
                    add-label-classes="required"
                    :is-valid="(value) => validField('second_grace_period', value)"
                    type="number"
                    min="0"
                    max="255"
                />
                <DAlert
                    v-if="errors && errors.second_grace_period"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.second_grace_period }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.second_due_days')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.second_due_days_hint')"
            >
                <DInput
                    id="second_due_days"
                    v-model="form.second_due_days"
                    add-label-classes="required"
                    :is-valid="(value) => validField('second_due_days', value)"
                    type="number"
                    min="0"
                    max="255"
                />
                <DAlert
                    v-if="errors && errors.second_due_days"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.second_due_days }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.enable_second_reminder_pdf')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.enable_reminder_pdf_helptext')"
            >
                <DSwitch
                    v-model="form.enable_second_reminder_pdf"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DCard
                :title="__('settings.dunning_settings.form.delivery_type')"
                padding
            >
                <DRow class="mx-auto mb-3">
                    {{ __('settings.dunning_settings.form.delivery_type_hint') }}
                </DRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_email')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_email_hint_second')"
                >
                    <DSwitch
                        v-model="form.second_delivery_type_email"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_postal')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_postal_hint_second')"
                >
                    <DSwitch
                        v-model="form.second_delivery_type_postal"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_electronic_invoice')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_electronic_invoice_hint')"
                >
                    <DSwitch
                        v-model="form.second_delivery_type_electronic_invoice"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_sms')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_sms_hint_second')"
                >
                    <DSwitch
                        v-model="form.second_delivery_type_sms"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_use_customer_settings')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_use_customer_settings_hint')"
                >
                    <DSwitch
                        v-model="form.second_delivery_type_use_customer_settings"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
        </DCard>
        <DCard
            :title="__('settings.dunning_settings.form.last_reminder')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.third_grace_period')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.third_grace_period_hint')"
            >
                <DInput
                    id="third_grace_period"
                    v-model="form.third_grace_period"
                    add-label-classes="required"
                    :is-valid="(value) => validField('third_grace_period', value)"
                    type="number"
                    min="0"
                    max="255"
                />
                <DAlert
                    v-if="errors && errors.third_grace_period"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.third_grace_period }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.third_due_days')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.third_due_days_hint')"
            >
                <DInput
                    id="third_due_days"
                    v-model="form.third_due_days"
                    add-label-classes="required"
                    :is-valid="(value) => validField('third_due_days', value)"
                    type="number"
                    min="0"
                    max="255"
                />
                <DAlert
                    v-if="errors && errors.third_due_days"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.third_due_days }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.enable_third_reminder_pdf')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.enable_reminder_pdf_helptext')"
            >
                <DSwitch
                    v-model="form.enable_third_reminder_pdf"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.suspend_contracts_on_third_level')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.suspend_contracts_on_third_level_hint')"
            >
                <DSwitch
                    v-model="form.suspend_contracts_on_third_level"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DCard
                :title="__('settings.dunning_settings.form.delivery_type')"
                padding
            >
                <DRow class="mx-auto mb-3">
                    {{ __('settings.dunning_settings.form.delivery_type_hint') }}
                </DRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_email')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_email_hint_last')"
                >
                    <DSwitch
                        v-model="form.third_delivery_type_email"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_postal')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_postal_hint_last')"
                >
                    <DSwitch
                        v-model="form.third_delivery_type_postal"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_electronic_invoice')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_electronic_invoice_hint')"
                >
                    <DSwitch
                        v-model="form.third_delivery_type_electronic_invoice"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_sms')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_sms_hint_last')"
                >
                    <DSwitch
                        v-model="form.third_delivery_type_sms"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_use_customer_settings')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_use_customer_settings_hint')"
                >
                    <DSwitch
                        v-model="form.third_delivery_type_use_customer_settings"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
        </DCard>
        <DCard
            :title="__('settings.dunning_settings.form.damage_compensation')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.damage_compensation_active')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.damage_compensation_active_hint')"
            >
                <DSwitch
                    v-model="form.damage_compensation_active"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                v-if="form.damage_compensation_active"
                :label="__('settings.dunning_settings.form.damage_compensation_cancellation_period')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.damage_compensation_cancellation_period_hint')"
            >
                <DInput
                    id="damage_compensation_cancellation_period"
                    v-model="form.damage_compensation_cancellation_period"
                    :is-valid="!errors.damage_compensation_cancellation_period"
                    type="number"
                    min="0"
                />
                <DAlert
                    v-if="errors && errors.damage_compensation_cancellation_period"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.damage_compensation_cancellation_period }}
                </DAlert>
            </DFormRow>
            <DFormRow
                v-if="form.damage_compensation_active"
                :label="__('settings.dunning_settings.form.damage_compensation_tax_exemption')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.damage_compensation_tax_exemption_hint')"
            >
                <DSelect
                    id="damage_compensation_tax_exemption"
                    v-model="form.damage_compensation_tax_exemption_id"
                    add-input-classes="bg-light"
                    :options="taxExemptions"
                    :placeholder="__('settings.dunning_settings.form.damage_compensation_tax_exemption')"
                    :is-valid="!errors.damage_compensation_tax_exemption_id"
                />
                <DAlert
                    v-if="errors && errors.damage_compensation_tax_exemption_id"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.damage_compensation_tax_exemption_id }}
                </DAlert>
            </DFormRow>
            <DFormRow
                v-if="form.damage_compensation_active"
                :label="__('settings.dunning_settings.form.damage_compensation_accounting_product')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.damage_compensation_accounting_product_hint')"
            >
                <DSelect
                    id="damage_compensation_accounting_product"
                    v-model="form.damage_compensation_accounting_product_id"
                    add-input-classes="bg-light"
                    :options="accountingProducts"
                    :placeholder="__('settings.dunning_settings.form.damage_compensation_accounting_product')"
                    :is-valid="!errors.damage_compensation_accounting_product_id"
                />
                <DAlert
                    v-if="errors && errors.damage_compensation_accounting_product_id"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.damage_compensation_accounting_product_id }}
                </DAlert>
            </DFormRow>
            <DFormRow
                v-if="form.damage_compensation_active"
                :label="__('settings.dunning_settings.form.damage_compensation_line_text')"
                :hint="__('settings.dunning_settings.form.damage_compensation_line_text_hint')"
            >
                <DInput
                    id="damage_compensation_line_text"
                    v-model="form.damage_compensation_line_text"
                    :is-valid="!errors.damage_compensation_line_text"
                    maxlength="255"
                />
                <TranslationLink
                    v-if="reminderSettings.id"
                    :tenant-languages="tenantLanguages"
                    :route="$route('settings.dunning-settings.translations', { reminderSetting: reminderSettings.id })"
                    :default-tenant-language="defaultTenantLanguage"
                />
                <DAlert
                    v-if="errors && errors.damage_compensation_line_text"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.damage_compensation_line_text }}
                </DAlert>
            </DFormRow>
        </DCard>
        <DCard
            :title="__('settings.dunning_settings.form.sepa_payment_retry.title')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.sepa_payment_retry.enable')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.sepa_payment_retry.enable_hint')"
            >
                <DSwitch
                    v-model="form.enable_sepa_payment_retry"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                v-if="form.enable_sepa_payment_retry"
                :label="__('settings.dunning_settings.form.sepa_payment_retry.btn-title')"
            >
                <DButton
                    id="sepa_payment_retry"
                    color="success"
                    :label="__('settings.dunning_settings.form.sepa_payment_retry.btn-title')"
                    @click="$inertia.get($route('settings.sepa-retry-failure-codes.index'))"
                />
            </DFormRow>
        </DCard>
        <DCard
            :title="__('settings.dunning_settings.form.termination')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.termination_grace_period')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.termination_grace_period_hint')"
            >
                <DInput
                    id="termination_grace_period"
                    v-model="form.termination_grace_period"
                    add-label-classes="required"
                    :is-valid="(value) => validField('termination_grace_period', value)"
                    type="number"
                    min="0"
                    max="255"
                />
                <DAlert
                    v-if="errors && errors.termination_grace_period"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.termination_grace_period }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.termination_reason')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.termination_reason_hint')"
            >
                <DSelect
                    id="termintion_reason"
                    v-model="form.termination_reason_id"
                    add-label-classes="required"
                    :options="terminationReasons"
                    :is-valid="(value) => validField('termination_reason_id', value)"
                    :placeholder="__('settings.dunning_settings.form.termination_reason_placeholder')"
                />
                <DAlert
                    v-if="errors && errors.termination_reason_id"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.termination_reason_id }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.enable_termination_reminder_pdf')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.enable_reminder_pdf_helptext')"
            >
                <DSwitch
                    v-model="form.enable_termination_reminder_pdf"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DCard
                :title="__('settings.dunning_settings.form.delivery_type')"
                padding
            >
                <DRow class="mx-auto mb-3">
                    {{ __('settings.dunning_settings.form.delivery_type_hint') }}
                </DRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_email')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_email_hint_termination')"
                >
                    <DSwitch
                        v-model="form.fourth_delivery_type_email"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_postal')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_postal_hint_termination')"
                >
                    <DSwitch
                        v-model="form.fourth_delivery_type_postal"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_electronic_invoice')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_electronic_invoice_hint')"
                >
                    <DSwitch
                        v-model="form.fourth_delivery_type_electronic_invoice"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_sms')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_sms_hint_termination')"
                >
                    <DSwitch
                        v-model="form.fourth_delivery_type_sms"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.delivery_type_use_customer_settings')"
                    :is-required="true"
                    :hint="__('settings.dunning_settings.form.delivery_type_use_customer_settings_hint')"
                >
                    <DSwitch
                        v-model="form.fourth_delivery_type_use_customer_settings"
                        color="success"
                        variant="3d"
                    />
                </DFormRow>
            </DCard>
            <DFormRow
                :label="__('settings.dunning_settings.form.enable_dunning_termination_cancellation_on_invoice_paid')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.enable_dunning_termination_cancellation_on_invoice_paid_hint')"
            >
                <DSwitch
                    v-model="form.enable_dunning_termination_cancellation_on_invoice_paid"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.use_last_paid_period_for_termination_date')"
                :is-required="true"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.use_last_paid_period_for_termination_date_hint')"
            >
                <DSwitch
                    v-model="form.use_last_paid_period_for_termination_date"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
        </DCard>
        <DCard
            :title="__('settings.dunning_settings.form.collection')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.collection_handover')"
                :is-required="true"
                :error="errors.collection_handover"
                :hint="__('settings.dunning_settings.form.collection_handover_hint')"
            >
                <DSwitch
                    v-model="form.collection_handover"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.enable_collection_reminder_pdf')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.enable_reminder_pdf_helptext')"
                :error="errors.enable_collection_reminder_pdf"
            >
                <DSwitch
                    v-model="form.enable_collection_reminder_pdf"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.enable_payment_of_invoices_in_debt_collection')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.enable_payment_of_invoices_in_debt_collection_hint')"
                :error="errors.enable_payment_of_invoices_in_debt_collection"
            >
                <DSwitch
                    v-model="form.enable_payment_of_invoices_in_debt_collection"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <!-- show only if collection handover is enabled-->
            <div v-if="form.collection_handover">
                <DFormRow
                    :label="__('settings.dunning_settings.form.collection_grace_period')"
                    :is-required="true"
                    :is-vertical="true"
                    :hint="__('settings.dunning_settings.form.collection_grace_period_hint')"
                >
                    <DInput
                        id="collection_grace_period"
                        v-model="form.collection_grace_period"
                        add-label-classes="required"
                        :is-valid="(value) => validField('collection_grace_period', value)"
                        type="number"
                        min="0"
                        max="255"
                    />
                    <DAlert
                        v-if="errors && errors.collection_grace_period"
                        color="danger"
                        class="mt-2"
                    >
                        {{ errors.collection_grace_period }}
                    </DAlert>
                </DFormRow>
                <DFormRow
                    :label="__('settings.dunning_settings.form.collection_partner')"
                    :is-required="true"
                    :is-vertical="true"
                    :hint="__('settings.dunning_settings.form.collection_partner_hint')"
                >
                    <DSelect
                        id="collection_partner"
                        v-model="form.collection_partner"
                        add-label-classes="required"
                        :options="collectionPartners"
                        :is-valid="!errors.collection_partner"
                    />
                    <DAlert
                        v-if="errors && errors.collection_partner"
                        color="danger"
                        class="mt-2"
                    >
                        {{ errors.collection_partner }}
                    </DAlert>
                </DFormRow>
                <DFormRow
                    v-if="form.collection_partner === 'CREFO'"
                    :label="__('settings.dunning_settings.form.member_no')"
                    :is-required="true"
                    :is-vertical="true"
                    :hint="__('settings.dunning_settings.form.member_no_hint')"
                >
                    <DInput
                        id="member_no"
                        v-model="form.member_no"
                        add-label-classes="required"
                        :is-valid="!errors.member_no"
                        type="number"
                    />
                    <DAlert
                        v-if="errors && errors.member_no"
                        color="danger"
                        class="mt-2"
                    >
                        {{ errors.member_no }}
                    </DAlert>
                </DFormRow>
            </div>
        </DCard>
        <DCard
            :title="__('settings.dunning_settings.form.more_settings')"
            padding
        >
            <DFormRow
                :label="__('settings.dunning_settings.form.send_collection_email')"
                :is-required="true"
                :hint="__('settings.dunning_settings.form.send_collection_email_hint')"
            >
                <DSwitch
                    v-model="form.send_collection_email"
                    color="success"
                    variant="3d"
                />
                <DAlert
                    v-if="errors && errors.send_collection_email"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.send_collection_email }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.accounting_product')"
                :is-required="false"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.accounting_product_hint')"
            >
                <DSelect
                    id="accounting_product"
                    v-model="form.accounting_product_id"
                    add-input-classes="bg-light"
                    :options="accountingProducts"
                    :placeholder="__('settings.dunning_settings.form.accounting_product')"
                    :is-valid="!errors.accounting_product_id"
                />
                <DAlert
                    v-if="errors && errors.accounting_product_id"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.accounting_product_id }}
                </DAlert>
            </DFormRow>
            <DFormRow
                :label="__('settings.dunning_settings.form.minimum_invoices_created_date')"
                :is-required="false"
                :is-vertical="true"
                :hint="__('settings.dunning_settings.form.minimum_invoices_created_date_hint')"
            >
                <DDateFormatInput
                    id="mmr_minimum_invoices_created_date"
                    v-model="form.minimum_invoices_created_date"
                    type="date"
                />
                <DAlert
                    v-if="errors && errors.minimum_invoices_created_date"
                    color="danger"
                    class="mt-2"
                >
                    {{ errors.minimum_invoices_created_date }}
                </DAlert>
            </DFormRow>
        </DCard>

        <DFormControls
            :key="reloadKey"
            :cancel-route="$route('settings.dunning-settings.create')"
            :dirty="form.isDirty"
            @submit="submit"
        />
    </div>
</template>

<script>
import TranslationLink from '~@/Components/Settings/TranslationLink.vue'

export default {
    name: 'ReminderSettingsCreate',
    components: { TranslationLink },
    props: {
        reminderSettings: {
            type: Object,
            default () {
                return {
                    first_due_days: null,
                    first_grace_period: null,
                    first_use_postal: false,
                    second_due_days: null,
                    second_grace_period: null,
                    second_use_postal: false,
                    third_due_days: null,
                    third_grace_period: null,
                    third_use_postal: false,
                    termination_grace_period: null,
                    collection_grace_period: null,
                    termination_reason_id: null,
                    termination_use_postal: false,
                    member_no: null,
                    collection_partner: null,
                    send_collection_email: null,
                    delivery_type_email: null,
                    delivery_type_postal: null,
                    delivery_type_electronic_invoice: null,
                    delivery_type_sms: null,
                    delivery_type_use_customer_settings: null,
                    second_delivery_type_email: null,
                    second_delivery_type_postal: null,
                    second_delivery_type_electronic_invoice: null,
                    second_delivery_type_sms: null,
                    second_delivery_type_use_customer_settings: null,
                    third_delivery_type_email: null,
                    third_delivery_type_postal: null,
                    third_delivery_type_electronic_invoice: null,
                    third_delivery_type_sms: null,
                    third_delivery_type_use_customer_settings: null,
                    fourth_delivery_type_email: null,
                    fourth_delivery_type_postal: null,
                    fourth_delivery_type_electronic_invoice: null,
                    fourth_delivery_type_sms: null,
                    fourth_delivery_type_use_customer_settings: null,
                    accounting_product_id: null,
                    collection_handover: true,
                    enable_first_reminder_pdf: true,
                    enable_second_reminder_pdf: true,
                    enable_third_reminder_pdf: true,
                    enable_termination_reminder_pdf: true,
                    enable_dunning_termination_cancellation_on_invoice_paid: false,
                    enable_collection_reminder_pdf: true,
                    attach_documents: ['attach_dunning_letter'],
                    minimum_invoices_created_date: null,
                    enable_payment_of_invoices_in_debt_collection: false,
                    suspend_contracts_on_third_level: false,
                    use_last_paid_period_for_termination_date: false,
                    damage_compensation_active: false,
                    damage_compensation_cancellation_period: null,
                    damage_compensation_tax_exemption_id: null,
                    damage_compensation_accounting_product_id: null,
                    damage_compensation_line_text: null,
                    enable_sepa_payment_retry: false
                }
            }
        },
        errors: {
            type: Object,
            default () {
                return {}
            }
        },
        terminationReasons: {
            type: Array,
            default () {
                return []
            }
        },
        collectionPartners: {
            type: Array,
            default () {
                return []
            }
        },
        accountingProducts: {
            type: Array,
            default () {
                return []
            }
        },
        taxExemptions: {
            type: Array,
            default () {
                return []
            }
        },
        dunningAttachDocuments: {
            type: Array,
            default: () => { return [] }
        },
        defaultTenantLanguage: {
            type: Object,
            default () {
                return {}
            }
        },
        tenantLanguages: {
            type: Array,
            default () {
                return []
            }
        }
    },
    data () {
        return {
            form: this.$inertia.form(Object.assign({}, this.reminderSettings)),
            reloadKey: 0
        }
    },
    computed: {
        getDunningAttachDocumentsOptions () {
            const dunningAttachDocuments = []

            this.dunningAttachDocuments.forEach((item) => {
                dunningAttachDocuments.push({
                    value: item.value,
                    text: item.label,
                    selected: this.form.attach_documents !== undefined &&
                        this.form.attach_documents.includes(item.value) === true
                })
            })

            return dunningAttachDocuments
        }
    },
    methods: {
        validField (fieldname, value) {
            return !this.errors[fieldname] && !isNaN(value) && value > 0 && value < 256
        },
        submit () {
            this.$inertia.post(
                this.$route('settings.dunning-settings.store'),
                this.form,
                { onSuccess: () => ++this.reloadKey }
            )
        },
        updateDunningAttachDocumentsValue (values) {
            this.form.attach_documents = values
        }
    }
}
</script>

<style lang="scss" scoped>
.page-dunning-settings {
    display: grid;
    grid-gap: 25px;
}
</style>
